import { PortableText } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from 'components/rebrand/Button';
import { Link } from 'components/rebrand/Link';

import CTA_BudgetTemplate from '../CTA_BudgetTemplate';
import { Body, SmallText } from '../Typography';
import Banner from './Banner';
import Figure from './Figure';
import Video from './Video';

const ListItem = ({ children }: { children?: ReactNode }) => (
  <li className="text-md md:text-lg">
    <Body className="mb-0 inline-block">{children}</Body>
  </li>
);

const Superscript = ({ children }: { children?: ReactNode }) => <sup>{children}</sup>;

const LinkObject = ({ value }) => (
  <Link href={value.url} className="w-full xs:w-auto">
    <Button className="w-full xs:w-auto">{value.text}</Button>
  </Link>
);

const CustomLink = ({ value, children }: { value?: { href?: string }; children: ReactNode }) => {
  return (
    <Link className="font-medium text-grey-400" href={value?.href ?? ''}>
      {children}
    </Link>
  );
};

const RawHtml = ({ value }) =>
  value.language === 'html' ? <Body dangerouslySetInnerHTML={{ __html: value.code }} /> : '';

const dropInComponentSerializer = ({ value }) => {
  switch (value.component) {
    case 'boxedContent':
      return <CTA_BudgetTemplate className="mb-16" />;
    case 'ctaBudgetTemplate':
      return <CTA_BudgetTemplate className="mb-16" />;
    default:
      return null;
  }
};

const CustomPortableText = ({ content, noLinks }: { content: PortableTextBlock; noLinks: boolean }) => {
  return (
    <PortableText
      onMissingComponent={false}
      value={content}
      components={{
        types: {
          image: ({ value }) => <img src={value.imageUrl} alt={value.alt} />,
          blockContentImg: ({ value }) => <Figure node={value} />,
          blockContentBanner: ({ value }) => <Banner node={value} />,
          dropInComponent: (props) => dropInComponentSerializer(props),
          code: RawHtml,
          video: ({ value }) => <Video node={value} />,
          linkObject: ({ value }) => <LinkObject value={value} />,
        },
        marks: {
          link: noLinks ? ({ children }) => <span>{children}</span> : CustomLink,
          internalLink: noLinks ? ({ children }) => <span>{children}</span> : CustomLink,
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          sup: Superscript,
          nbsp: ({ children }) => <span className="whitespace-nowrap">{children}</span>,
        },
        block: {
          h1: ({ children }) => (
            <h1 className="mb-20 text-4xl font-bold leading-130 text-grey-450 md:text-5xl">{children}</h1>
          ),
          h2: ({ children }) => <h2 className="mb-20 text-4xl font-bold leading-130 text-grey-450">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-20 text-4xl font-bold leading-130 text-grey-450">{children}</h3>,
          h4: ({ children }) => (
            <h4 className="mb-16 text-xl font-bold leading-130 text-grey-450 md:text-2xl">{children}</h4>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-64 text-3xl font-normal leading-tight tracking-normal md:text-6xl">
              {children}
            </blockquote>
          ),
          normal: ({ children }) => <Body>{children}</Body>,
          small: ({ children }) => <SmallText>{children}</SmallText>,
        },
        list: {
          bullet: ({ children }) => <ul className="mb-16 ml-16 list-inside list-disc">{children}</ul>,
          number: ({ children }) => <ol className="mb-16 ml-16 list-inside list-decimal">{children}</ol>,
        },
        listItem: {
          bullet: ListItem,
          number: ListItem,
        },
      }}
    />
  );
};

const BlockContent = ({
  blocks,
  className,
  id,
  noLinks = false,
}: {
  blocks: any;
  className?: string;
  id?: string;
  noLinks?: boolean;
}) => (
  <div
    id={id}
    className={twMerge(
      clsx(
        '[&_*:last-child]:mb-0 [&_li_p]:inline',
        '[&_ol+h2]:mt-36 md:[&_ol+h2]:mt-48 [&_ol+h3]:mt-32 md:[&_ol+h3]:mt-44 [&_p+h2]:mt-36 md:[&_p+h2]:mt-48 [&_p+h3]:mt-32 md:[&_p+h3]:mt-44 [&_p+h4]:mt-28 md:[&_p+h4]:mt-36',
        '[&_ul+h2]:mt-36 md:[&_ul+h2]:mt-48 [&_ul+h3]:mt-32 md:[&_ul+h3]:mt-44 [&_ul+h4]:mt-28 md:[&_ul+h4]:mt-36',
        '[&_strong_em]:not-italic [&_strong_em]:text-primary-300',
        className,
      ),
    )}
  >
    <CustomPortableText content={blocks} noLinks={noLinks} />
  </div>
);

export default BlockContent;
